// Assistência do editor de autoria IDS a partir do modelo carregado:
// que classes IFC existem mesmo, que property sets e propriedades, que
// materiais e sistemas de classificação. Alimenta os <datalist> do
// editor para o utilizador escolher nomes que existem em vez de os
// escrever de cor.
//
// Deliberadamente LEVE e PREGUIÇOSO: não constrói nenhum índice novo
// pesado nem duplica o trabalho do idsEngine. Só é chamado quando o
// utilizador abre um campo com assistência, e o resultado fica em cache
// por modelo (a estrutura de um IFC carregado não muda).
//
// As classes vêm do próprio web-ifc (GetAllTypesOfModel, uma chamada
// nativa); os psets/propriedades de uma passagem única pelos
// IfcPropertySet / IfcElementQuantity, que são poucos comparados com o
// número de elementos.

import { rawIfc } from '@viewer/lib/rawIfc'

export interface ModelSuggestions {
  /** Classes IFC presentes no modelo, em MAIÚSCULAS (ex.: IFCWALL). */
  entities: string[]
  /** Nomes de property set / element quantity. */
  psets: string[]
  /** Nome do pset -> propriedades desse pset. */
  propertiesByPset: Map<string, string[]>
  /** Todas as propriedades, sem duplicados (para quando não há pset
   *  escolhido). */
  properties: string[]
  materials: string[]
  classificationSystems: string[]
}

export const EMPTY_SUGGESTIONS: ModelSuggestions = {
  entities: [],
  psets: [],
  propertiesByPset: new Map(),
  properties: [],
  materials: [],
  classificationSystems: [],
}

/** Limite de segurança para modelos patológicos: passada a marca, ficamos
 *  com o que já recolhemos (as sugestões são ajuda, não exaustividade). */
const MAX_PSETS = 20_000

const cache = new Map<string, Promise<ModelSuggestions>>()

function nameOf(o: unknown): string | null {
  if (!o || typeof o !== 'object') return null
  const v = (o as { Name?: unknown }).Name
  if (typeof v === 'string') return v.trim() || null
  if (v && typeof v === 'object' && 'value' in (v as object)) {
    const inner = (v as { value?: unknown }).value
    if (typeof inner === 'string') return inner.trim() || null
  }
  return null
}

function sortedUnique(set: Set<string>): string[] {
  return [...set].sort((a, b) => a.localeCompare(b, 'pt'))
}

function collectEntities(modelId: string): string[] {
  const handles = rawIfc.getRawHandles(modelId)
  if (!handles) return []
  try {
    const types = handles.api.GetAllTypesOfModel(handles.modelID)
    const out = new Set<string>()
    for (const t of types) {
      if (t?.typeName) out.add(String(t.typeName).toUpperCase())
    }
    return sortedUnique(out)
  } catch {
    return []
  }
}

function collectProperties(modelId: string): {
  psets: string[]
  propertiesByPset: Map<string, string[]>
  properties: string[]
} {
  const byPset = new Map<string, Set<string>>()
  const all = new Set<string>()
  let seen = 0
  for (const cls of ['IFCPROPERTYSET', 'IFCELEMENTQUANTITY']) {
    let ids: number[]
    try {
      ids = rawIfc.getLineIDsByType(modelId, cls)
    } catch {
      continue
    }
    for (const id of ids) {
      if (seen >= MAX_PSETS) break
      seen += 1
      const pset = rawIfc.getEntity(modelId, id) as Record<
        string,
        unknown
      > | null
      if (!pset) continue
      const psetName = nameOf(pset)
      if (!psetName) continue
      let bucket = byPset.get(psetName)
      if (!bucket) {
        bucket = new Set<string>()
        byPset.set(psetName, bucket)
      }
      for (const key of ['HasProperties', 'Quantities']) {
        const arr = pset[key]
        if (!Array.isArray(arr)) continue
        for (const p of arr) {
          const pn = nameOf(p)
          if (!pn) continue
          bucket.add(pn)
          all.add(pn)
        }
      }
    }
  }
  const propertiesByPset = new Map<string, string[]>()
  for (const [k, v] of byPset) propertiesByPset.set(k, sortedUnique(v))
  return {
    psets: sortedUnique(new Set(byPset.keys())),
    propertiesByPset,
    properties: sortedUnique(all),
  }
}

function collectNamesOfClass(modelId: string, cls: string): string[] {
  let ids: number[]
  try {
    ids = rawIfc.getLineIDsByType(modelId, cls)
  } catch {
    return []
  }
  const out = new Set<string>()
  for (const id of ids) {
    const n = nameOf(rawIfc.getEntity(modelId, id))
    if (n) out.add(n)
  }
  return sortedUnique(out)
}

async function build(modelId: string): Promise<ModelSuggestions> {
  if (!rawIfc.hasModel(modelId)) await rawIfc.loadModel(modelId)
  const props = collectProperties(modelId)
  return {
    entities: collectEntities(modelId),
    psets: props.psets,
    propertiesByPset: props.propertiesByPset,
    properties: props.properties,
    materials: collectNamesOfClass(modelId, 'IFCMATERIAL'),
    classificationSystems: collectNamesOfClass(modelId, 'IFCCLASSIFICATION'),
  }
}

/**
 * Sugestões do modelo, calculadas à primeira chamada e reutilizadas
 * daí em diante. Chamar só quando o utilizador precisa mesmo (abrir um
 * campo com assistência), nunca no arranque da página.
 */
export function getModelSuggestions(
  modelId: string,
): Promise<ModelSuggestions> {
  const hit = cache.get(modelId)
  if (hit) return hit
  const p = build(modelId).catch(() => EMPTY_SUGGESTIONS)
  cache.set(modelId, p)
  return p
}

/** Esquece as sugestões de um modelo (ex.: modelo descarregado). */
export function forgetModelSuggestions(modelId: string): void {
  cache.delete(modelId)
}
